// Vendor
import * as React from 'react';

// Internal
import { IconArrow } from '@src/svg/IconArrow';
import { Image } from '@src/components/Image/Image';
import { DEFAULT_SAL_PROPS, SAL_DELAY } from '@src/config/sal';
import { SPOT_GREEN } from '@thredup/styleguide/src/variables/colors';

// Styles
import styles from './styles.module.css';

const RetailSeeksToJoin: React.FC = () => {
  return (
    <div className="tup-slide">
      <h1 {...DEFAULT_SAL_PROPS} className="max-w-2xl">
        Retail Seeks to Join Resale Boom
      </h1>
      <p
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={SAL_DELAY * 1}
        className="tup-body "
      >
        Circular partnerships drive revenue, build brand awareness, and increase
        customer loyalty.{' '}
      </p>
      <h2
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={SAL_DELAY * 2}
        className="mb-8 mt-8 max-w-md"
      >
        Top reasons retail executives are interested in testing resale.
        <sup>5</sup>
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-24">
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 3}>
          <div className="flex items-center">
            <p className="block float-left tup-number font-bold pr-4">82%</p>
            <p>
              to increase
              <br />
              foot traffic
            </p>
          </div>
          <div className={styles.meter}>
            <div className={styles.bar} style={{ width: '82%' }}></div>
          </div>
        </div>
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 4}>
          <div className="flex items-center">
            <p className="block float-left tup-number font-bold pr-4">66%</p>
            <p>
              for the
              <br />
              environment
            </p>
          </div>
          <div className={styles.meter}>
            <div className={styles.bar} style={{ width: '66%' }}></div>
          </div>
        </div>
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 5}>
          <div className="flex items-center">
            <p className="block float-left tup-number font-bold pr-4">58%</p>
            <p>
              to appeal to a younger
              <br />
              demographic
            </p>
          </div>
          <div className={styles.meter}>
            <div className={styles.bar} style={{ width: '58%' }}></div>
          </div>
        </div>
      </div>

      <h2
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={SAL_DELAY * 2}
        className="mb-8 mt-12"
      >
        Unlimited Retail x Resale Opportunities
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 3}>
          <Image
            alt="Madewell with thredUP jeans"
            className="m-auto"
            fileName="resale-shop.jpg"
            maxWidth={208}
          />
          <p className="text-center mt-4">
            <strong>RESALE SHOP</strong>
            <br />
            Create a custom resale experience online or in-store.
          </p>
        </div>
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 4}>
          <Image
            alt="Thredup Cleanout partners"
            className="m-auto"
            fileName="cleanout.jpg"
            maxWidth={208}
          />
          <p className="text-center mt-4 pr-4 pl-4">
            <strong>CLEAN OUT</strong>
            <br />
            Provide Clean Out Kits to your customers so they can turn their
            clothes into credit to shop with your brand.
          </p>
        </div>
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 5}>
          <Image
            alt="Thredup Parther Cleanout Kit"
            className="m-auto"
            fileName="cashout.jpg"
            maxWidth={208}
          />
          <p className="text-center mt-4 pr-4 pl-4">
            <strong>CASH OUT</strong>
            <br />
            Give thredUP sellers the option to cash out with credit to shop with
            your brand.
          </p>
        </div>
        <div {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 6}>
          <Image
            alt="Northface Jacket"
            className="m-auto"
            fileName="excess-inventory.jpg"
            maxWidth={208}
          />
          <p className="text-center mt-4 pr-4 pl-4">
            <strong>EXCESS INVENTORY</strong>
            <br />
            Turn your unused and returned inventory into additional revenue.
          </p>
        </div>
      </div>
      <div
        {...DEFAULT_SAL_PROPS}
        data-sal="fade"
        data-sal-delay={SAL_DELAY * 5}
        className="bg-cleanout-background p-12 grid grid-cols-1 md:grid-cols-2 gap-10 mt-12"
      >
        <blockquote
          {...DEFAULT_SAL_PROPS}
          data-sal-delay={SAL_DELAY * 5}
          className="md:pr-8 md:border-r-2 md:border-right-solid border-cleanout-light"
        >
          <p className="text-base">
            &ldquo;If I’m a fashion brand right now and see that the resale
            industry is growing at a rate that’s 21 times faster than the
            overall fashion industry, I’d be thinking to myself, “How do I get a
            piece of this?&rdquo;
          </p>
          <footer className="font-bold">
            <Image
              alt="BOF Logo"
              className="mt-2"
              fileName="bof-logo.png"
              maxWidth={52}
            />
          </footer>
        </blockquote>
        <blockquote {...DEFAULT_SAL_PROPS} data-sal-delay={SAL_DELAY * 6}>
          <p className="text-base">
            &ldquo;Partnering with thredUP gives Walmart a slice of the action
            in the resale space, which has been growing rapidly. With
            secondhand, Walmart can attract younger customers who are more
            planet and cost conscious.&rdquo;
          </p>
          <footer className="font-bold">
            <Image
              alt="GlobalData Logo"
              className="mt-2"
              fileName="globaldata-logo.png"
              maxWidth={125}
            />
          </footer>
        </blockquote>
      </div>
      <p
        {...DEFAULT_SAL_PROPS}
        data-sal-delay={SAL_DELAY * 6}
        className="text-center mt-12"
      >
        Join the most powerful resale platform in the world.
        <a
          href="https://www.thredup.com/resale-as-a-service"
          className="uppercase ml-4 mt-2 text-sm flex font-bold text-green items-center justify-center"
          target="_blank"
          rel="noopener noreferrer"
        >
          Get Started{' '}
          <IconArrow
            className={styles.arrow}
            direction="right"
            fill={SPOT_GREEN}
            height={20}
            width={30}
          />
        </a>
      </p>
    </div>
  );
};

export { RetailSeeksToJoin };
